import React, { ReactElement, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { EFormInputTypes, FormInput } from 'components/molecules/FormInput';
import { Resolver, useForm } from 'react-hook-form';
import { initialLoginFormValues } from 'models/form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLoginSchema } from 'pages/LoginPage/schemaValidation';
import { EButtonVariants } from 'constants/Buttons';
import { RouterLink } from 'components/atoms/RouterLink';
import { useAuth } from 'models/authContext';
import { IAuthBodyRequest, IAuthResponse, ILoginForm } from 'models/auth';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { ERouteLinks } from 'models/route';
import { Box } from '@mui/system';
import { Alert } from 'components/atoms/Alert';
import { AuthTemplate, StyledAuthHeader } from 'components/templates/AuthTemplate';
import { EStep, IData } from 'pages/AuthPage';
import { useNavigate } from 'react-router-dom';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { useLicenceContext } from 'models/licenceContext';
import { getDeviceName } from 'helpers/userAgent';
import { useTranslations } from 'hooks/useTranslations';
import {
  StyledLanguageSelectOuterWrapper,
  StyledLanguageSelectWrapper,
} from 'components/templates/AuthTemplate/styles';
import { LanguageSelect } from 'components/molecules/LanguageSelect';
import { AuthLogoContainer } from 'components/organisms/AuthLogoContainer';

export const FC_SYSTEM_DEVICE_ID = 'FC_SYSTEM_DEVICE_ID';

export interface ILoginPageProps {
  changePage: (p: EStep) => void;
  changeDataCallback: (p: IData) => void;
}

const LoginPage = ({ changePage, changeDataCallback }: ILoginPageProps): ReactElement => {
  const navigate = useNavigate();
  const { t } = useTranslations();
  const { schema } = useLoginSchema();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ILoginForm>({
    defaultValues: initialLoginFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<ILoginForm>,
  });
  const { logIn, authMessage, isLoginLoading, clearAuthErrors } = useAuth();
  const { authAlert, clearAlert } = useAlert();
  const { isOnlineLicence } = useLicenceContext();

  useEffect(
    () => () => {
      clearAuthErrors();
    },
    [],
  );
  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_AUTH_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onSubmit = (data: ILoginForm) => {
    const body: IAuthBodyRequest = {
      ...data,
      deviceToken: localStorage.getItem(FC_SYSTEM_DEVICE_ID) || null,
      deviceName: getDeviceName(),
    };
    logIn(body, (res: IAuthResponse) => {
      if (res.accessToken) {
        navigate(ERouteLinks.Dashboard);
        changePage(EStep.dashboard);
      } else if (res.phoneNumber) {
        changePage(EStep.twoFactorAuth);
        const phoneNumber = res.phoneNumber.toString();
        const numberEndPart = phoneNumber.slice(phoneNumber.length - 3);
        changeDataCallback({
          ...data,
          phoneNumber: `*********${numberEndPart}`,
          offlineQRCodeBase64: null,
        });
      } else if (res.offlineQRCodeBase64 || res.offlineQRCodeBase64 === null) {
        changePage(EStep.authenticator);
        changeDataCallback({
          ...data,
          offlineQRCodeBase64: res.offlineQRCodeBase64 ?? null,
          phoneNumber: '',
        });
      } else {
        changePage(EStep.twoFactorAuth);
        changeDataCallback({
          ...data,
          phoneNumber: '',
          offlineQRCodeBase64: null,
        });
      }
    });
  };
  return (
    <AuthTemplate>
      <Grid
        container
        size={{ xs: 12, sm: 10, md: 7, lg: 6, xl: 6 }}
        sx={{
          display: 'grid',
          py: 2,
          px: { xs: 2, xl: 20 },
          justifyContent: 'center',
          justifyItems: 'center',
          alignItems: 'flex-start',
          alignContent: 'stretch',
          pb: 5,
        }}
      >
        <Grid size={{ xs: 12 }}>
          <StyledLanguageSelectOuterWrapper>
            <StyledLanguageSelectWrapper>
              <LanguageSelect isVisible />
            </StyledLanguageSelectWrapper>
          </StyledLanguageSelectOuterWrapper>
          <AuthLogoContainer />
        </Grid>

        <Grid
          container
          size={{ xs: 12 }}
          sx={{ justifyContent: 'center', alignItems: 'flex-start' }}
        >
          <Grid sx={{ mt: 1 }} size={{ xs: 12 }}>
            {authAlert && (
              <Box sx={{ mb: 3, mt: 2 }}>
                <Alert text={authAlert.text} variant={authAlert.variant} />
              </Box>
            )}
            {authMessage.text && !authAlert && (
              <Box sx={{ mb: 3, mt: 2 }}>
                <Alert text={authMessage.text} variant={authMessage.variant} />
              </Box>
            )}
            <StyledAuthHeader style={{ marginBottom: '32px' }}>
              {t('login.welcome.label')}
            </StyledAuthHeader>
            <Typography variant="body1" sx={{ textAlign: 'center', mb: 3 }}>
              {t('login.title1.label')}
              <br />
              {t('login.title2.label')}
            </Typography>
          </Grid>
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
            <Grid sx={{ justifyContent: 'center' }}>
              <Grid size={{ xs: 12 }}>
                <FormInput
                  name="email"
                  label={t('login.email.label')}
                  placeholder="name@domain.com"
                  control={control}
                  errors={errors}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: { xs: 32, sm: 40 },
                    },
                  }}
                />
                <FormInput
                  name="password"
                  type={EFormInputTypes.password}
                  label={t('login.password.label')}
                  placeholder={t('login.password.label')}
                  control={control}
                  errors={errors}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: { xs: 32, sm: 40 },
                    },
                  }}
                />
              </Grid>
              <Grid container sx={{ justifyContent: 'center' }}>
                <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
                  <Button
                    id="login"
                    type="submit"
                    label={t('login.login.button')}
                    variant={EButtonVariants.contained}
                    isLoading={isLoginLoading}
                    fullWidth
                  />
                </Grid>
                {isOnlineLicence() && (
                  <RouterLink
                    to={ERouteLinks.ForgotPassword}
                    label={t('login.forgotPassword.button')}
                  />
                )}
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </AuthTemplate>
  );
};
export { LoginPage };
